import React, { useState, Dispatch, SetStateAction } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Toolbar,
  Typography,
  Divider,
  Box,
  Switch,
  InputBase,
  Tooltip,
  ListItemButton,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/inventory.png";
import { useSelector } from "react-redux";
// Make sure to assign this if you plan to use the custom styles
import { useStyles } from "../../assets/styles/Theme.tsx";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useTheme } from "@mui/material/styles";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";

const drawerWidth = 240;
const collapsedWidth = 0;

// Corrected to match what PageRouting.tsx is passing
interface SidebarProps {
  mobileOpen?: boolean;
  handleDrawerToggle?: () => void;
  desktopOpen: boolean;
  setDesktopOpen?: Dispatch<SetStateAction<boolean>>;
  toggleTheme: () => void;
}

interface AccessRightItem {
  MAINMENU: string;
  MAINMENUORDER: number;
  SUBMENU?: string;
  SUBMENUORDER: number;
  MODMENUNAME?: string;
  URL: string;
  ICONPATH?: string;
  MODULECODE?: string;
}

interface RootState {
  accessRights: AccessRightItem[] | Record<string, AccessRightItem>;
  user: any;
}

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  handleDrawerToggle,
  desktopOpen,
  setDesktopOpen,
  toggleTheme,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme); // Assigned to 'classes' so you can use it later
  const location = useLocation();

  const [selectedTab, setSelectedTab] = useState<string>(location.pathname);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  const accessRights = useSelector((state: RootState) => state.accessRights);
  const SYS_USER = useSelector((state: RootState) => state.user) || {};
  const { COMPANYLOGO, COMPNAME } = SYS_USER.USER_PARAM || {};

  const handleTabClick = (path: string) => {
    setSelectedTab(path);
  };

  const handleSubMenuClick = (menu: string) => {
    setOpenSubMenu(openSubMenu === menu ? null : menu);
  };

  const accessRightsArray: AccessRightItem[] = Array.isArray(accessRights)
    ? accessRights
    : Object.values(accessRights || {}).filter(
        (item) => item.MODULECODE !== undefined
      );

  // Grouping the access rights
  const groupedAccessRights = accessRightsArray.reduce<
    Record<
      string,
      {
        MAINMENUORDER: number;
        items: {
          text: string;
          path: string;
          order: number;
        }[];
      }
    >
  >((acc, curr) => {
    const { MAINMENU, MAINMENUORDER, SUBMENU, SUBMENUORDER, MODMENUNAME, URL } =
      curr;

    if (!acc[MAINMENU]) {
      acc[MAINMENU] = {
        MAINMENUORDER,
        items: [],
      };
    }

    acc[MAINMENU].items.push({
      text: SUBMENU || MODMENUNAME || "",
      path: String(URL),
      order: SUBMENUORDER,
    });

    return acc;
  }, {});

  const sortedGroupedAccessRights = Object.entries(groupedAccessRights).sort(
    (a, b) => a[1].MAINMENUORDER - b[1].MAINMENUORDER
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: desktopOpen ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: desktopOpen ? drawerWidth : collapsedWidth,
          transition: "width 0.3s ease-in-out",
          borderRight: "none",
          overflowX: "hidden",
          backgroundColor: theme.palette.background.paper,
          boxShadow: `1px 0px 1px rgba(0, 0, 0, 0.1)`,
        },
      }}
      open
    >
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Toolbar sx={{ justifyContent: "center", p: 2 }}>
          <img
            src={COMPANYLOGO || logo}
            alt="Logo"
            style={{ width: 40, height: 40 }}
          />
          {desktopOpen && (
            <Box ml={2}>
              <Tooltip title={COMPNAME} arrow>
                <Typography variant="h5" noWrap>
                  {COMPNAME}
                </Typography>
              </Tooltip>
              <Typography variant="body_xxs">V1.1</Typography>
            </Box>
          )}
        </Toolbar>

        {/* SEARCH */}
        <Box px={1}>
          <InputBase
            placeholder="Ctrl + K"
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: 1,
              px: 1,
              height: 36,
              display: "flex",
            }}
            startAdornment={<SearchIcon sx={{ mr: 1 }} />}
          />
        </Box>

        {/* MENU */}
        <List sx={{ flexGrow: 1 }}>
          {sortedGroupedAccessRights.map(([mainMenu, { items }]) => (
            <React.Fragment key={mainMenu}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSubMenuClick(mainMenu)}>
                  {desktopOpen && (
                    <ListItemText
                      primary={mainMenu}
                      primaryTypographyProps={{
                        variant: "body_xs",
                      }}
                    />
                  )}
                  {items.length > 0 &&
                    (openSubMenu === mainMenu ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    ))}
                </ListItemButton>
              </ListItem>

              <Collapse
                in={openSubMenu === mainMenu}
                timeout="auto"
                unmountOnExit
              >
                <List disablePadding>
                  {items
                    .sort((a, b) => a.order - b.order)
                    .map(({ text, path }) => (
                      <ListItem key={text} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={path}
                          selected={selectedTab === path}
                          onClick={() => handleTabClick(path)}
                          sx={{ pl: 4 }}
                        >
                          <FiberManualRecordOutlinedIcon
                            sx={{ fontSize: 10, mr: 1 }}
                          />
                          <ListItemText
                            primary={text}
                            primaryTypographyProps={{
                              variant: "body_xs",
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>

        <Divider />

        {/* THEME SWITCH */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
          }}
        >
          <Box display="flex" alignItems="center">
            {theme.palette.mode === "dark" ? (
              <DarkModeIcon />
            ) : (
              <LightModeIcon />
            )}
            <Typography ml={1}>
              {theme.palette.mode === "dark" ? "Dark Mode" : "Light Mode"}
            </Typography>
          </Box>
          <Switch
            checked={theme.palette.mode === "dark"}
            onChange={toggleTheme}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
